import { useNavigate, useSearch } from "@tanstack/react-router";
import { Badge, Button } from "@/deps/ui-library";
import { useInvoices } from "@/api/queries";
import type { InvoiceSummary } from "@/api/types";
import type { Column } from "@/components/list/DataTable";
import { DocumentStatusBadge, PaymentStatusBadge } from "@/components/list/StatusBadge";
import type { ListSearch } from "@/components/list/listQuery";
import { ListScreen, listQueryFor } from "@/components/list/ListScreen";
import { formatAmount } from "@/lib/money";
import { formatDate } from "@/lib/dates";

const PAYMENTS = [
  { value: "all", label: "All payments" },
  { value: "paid", label: "Paid" },
  { value: "partiallyPaid", label: "Partially paid" },
  { value: "overdue", label: "Overdue" },
  { value: "unpaid", label: "Unpaid" },
];

export function InvoicesListPage() {
  const search = useSearch({ from: "/sales/invoices" }) as ListSearch;
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useInvoices(listQueryFor(search));

  const columns: Array<Column<InvoiceSummary>> = [
    {
      key: "number",
      header: "Number",
      width: "12ch",
      numeric: true,
      render: (row) => row.number ?? <span className="text-fg-hint">Not yet issued</span>,
    },
    {
      key: "customer",
      header: "Customer",
      render: (row) => (
        <span className="flex items-center gap-1.5">
          {row.customerName}
          {/* Locked and Rigo Billing are different facts: one says the document cannot be
              edited, the other says it was not raised here. */}
          {row.locked ? (
            <Badge colorScheme="default" size="sm">
              Locked
            </Badge>
          ) : null}
        </span>
      ),
    },
    { key: "date", header: "Date", width: "12ch", render: (row) => formatDate(row.date) },
    {
      key: "due",
      header: "Due",
      width: "12ch",
      render: (row) => (row.due ? formatDate(row.due) : ""),
    },
    {
      key: "status",
      header: "Status",
      width: "18ch",
      render: (row) => (
        <span className="flex items-center gap-1.5">
          <DocumentStatusBadge status={row.status} />
          {row.awaitingStep ? (
            <span className="text-fg-hint text-xs">{row.awaitingStep}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: "payment",
      header: "Payment",
      width: "14ch",
      render: (row) => <PaymentStatusBadge status={row.payment} />,
    },
    {
      key: "total",
      header: "Amount",
      align: "right",
      numeric: true,
      render: (row) => formatAmount(row.total),
    },
    {
      key: "balance",
      header: "Balance",
      align: "right",
      numeric: true,
      render: (row) => formatAmount(row.balance),
    },
  ];

  return (
    <ListScreen
      route="/sales/invoices"
      title="Invoices"
      subtitle={data ? `${data.total} this fiscal year` : "Loading invoices"}
      actions={
        <span className="flex gap-2">
          <Button
            variant="secondary"
            leftIcon="Plus"
            size="sm"
            onClick={() => void navigate({ to: "/sales/invoices/new" })}
          >
            New invoice
          </Button>
        </span>
      }
      columns={columns}
      rows={data?.items ?? []}
      total={data?.total ?? 0}
      hasNext={data?.next != null}
      loading={isPending}
      error={error}
      onRetry={() => void refetch()}
      rowKey={(row) => row.id}
      rowLabel={(row) =>
        `Invoice ${row.number ?? "not yet issued"}, ${row.customerName}, ${formatAmount(row.total)}`
      }
      onRowClick={(row) => void navigate({ to: "/sales/invoices/$id", params: { id: row.id } })}
      searchPlaceholder="Search number, customer or reference"
      payments={PAYMENTS}
      empty={{
        icon: "FileText",
        title: "No invoices yet",
        description:
          "Invoices raised in Rigo Billing appear here once they are issued. You can also sync them manually.",
      }}
      filteredEmpty={{
        icon: "Search",
        title: "No invoices match these filters",
        description: "Try a wider date range, or clear the filters to see everything again.",
      }}
    />
  );
}
